import { m } from "#/paraglide/messages";
import { Badge, createDataTableColumnHelper, DataTable } from "@beweegbaas/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { getInsightsActivityDetail } from "#/modules/klassen.insights/insights.functions";
import {
  OverachievedIconButton,
  ScoreStatusIndicatorLabel,
} from "#/modules/util.score-status/components/score-status";
import { getScoreStatus } from "#/modules/util.score-status/score-status";

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/lessons/te-behandelen/drawer/$beweegactiviteitId",
)({
  // No search params of its own — `klasId`/`from`/`to` are inherited from the parent
  // `lessons/te-behandelen` route's `validateSearch`. Narrowed to exactly the keys the loader uses.
  loaderDeps: ({ search }) => ({ klasId: search.klasId, from: search.from, to: search.to }),
  loader: ({ deps, params }) =>
    getInsightsActivityDetail({
      data: {
        klasId: deps.klasId,
        beweegactiviteitId: params.beweegactiviteitId,
        from: deps.from,
        to: deps.to,
      },
    }),
  component: ActivityDetail,
  errorComponent: ActivityDetailError,
});

type LeerlingRow = NonNullable<
  NonNullable<Awaited<ReturnType<typeof getInsightsActivityDetail>>["detail"]>["leerlingen"]
>[number];

const helper = createDataTableColumnHelper<LeerlingRow>();

const columns = helper.columns([
  helper.accessor("displayName", {
    header: "Leerling",
    meta: { cellClassName: "py-3" },
  }),
  helper.display({
    id: "resultaat",
    header: "Resultaat",
    meta: { cellClassName: "py-3" },
    cell: ({ row }) => {
      const leerling = row.original;
      return leerling.behaaldNiveau != null && leerling.behaaldNiveau > 0 ? (
        <span className="inline-flex items-center gap-1.5">
          <ScoreStatusIndicatorLabel
            status={getScoreStatus(leerling.behaaldNiveau, leerling.maxNiveau ?? 0)}
            label={leerling.resultaat}
          />
          {leerling.isMaxNiveau && <OverachievedIconButton />}
        </span>
      ) : (
        <Badge variant="gray" size="sm">
          -
        </Badge>
      );
    },
  }),
  helper.display({
    id: "les",
    header: () => null,
    meta: { rowTarget: true, headerClassName: "w-10", cellClassName: "py-3" },
    cell: ({ row }) =>
      row.original.lesId ? (
        <Badge
          variant="gray"
          size="sm"
          render={
            <Link
              from={Route.fullPath}
              to="/$locale/{-$schoolyear}/$school/lesson/$lesson/teacher/scores"
              params={(prev: Record<string, string>) => ({ ...prev, lesson: row.original.lesId! })}
            />
          }
        >
          Les
        </Badge>
      ) : (
        <Badge variant="gray" size="sm">
          -
        </Badge>
      ),
  }),
]);

function ActivityDetail() {
  const { detail } = Route.useLoaderData();

  if (!detail?.leerlingen || detail.leerlingen.length === 0) {
    return (
      <div className="px-10 pt-10 pb-10">
        <p className="text-gray-500">{m["lessen.te_behandelen.geen_leerlingen"]()}</p>
      </div>
    );
  }

  return (
    <section className="pt-10">
      <h3 className="m-0 mb-4 px-4 pr-10">{m["lessen.te_behandelen.details"]()}</h3>
      <DataTable
        className="-mr-6"
        aria-label={m["lessen.leerlingen_resultaten_aria"]()}
        columns={columns}
        data={detail.leerlingen}
        getRowId={(leerling) => leerling.leerlingId}
      />
    </section>
  );
}

function ActivityDetailError({ error }: { error: unknown }) {
  const params = Route.useParams();
  return <ErrorBoundaryComponent error={error} params={params} />;
}
